import { Fragment, useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarPerson, F0AvatarTeam, F0Icon } from '@factorialco/f0-react'
import { CheckCircle } from '@factorialco/f0-react/icons/app'

import buildUrl from 'modules/core/lib/buildUrl'

import OverflowTooltip from './OverflowTooltip'
import type { ApprovalsV2TimelineEntryAssignee, TimelineItemEmployeeFragment } from './types'
import styles from './styles.module.css'

const INLINE_DISPLAY_LIMIT = 3

const renderAssignee = (
  assignee: ApprovalsV2TimelineEntryAssignee,
  index: number,
  employees: TimelineItemEmployeeFragment[]
) => {
  if (assignee.peopleGroupName) {
    return (
      <Fragment key={`assignee-${assignee.peopleGroupName}-${index}`}>
        <F0AvatarTeam name={assignee.peopleGroupName} size="small" />
        <Text.Normal size="200" color="grey1000">
          {assignee.peopleGroupName}
        </Text.Normal>
      </Fragment>
    )
  }

  if (!assignee.employeeId) {
    return null
  }

  const employee = employees.find((emp) => emp?.id === assignee.employeeId)
  if (!employee) {
    return null
  }

  return (
    <Fragment key={`assignee-${assignee.employeeId}-${index}`}>
      <div className={styles.avatarWithBadge}>
        <F0AvatarPerson
          src={buildUrl(employee.avatar?.url)}
          firstName={employee.firstName || ''}
          lastName={employee.lastName || ''}
          size="small"
        />
        {assignee.completed && (
          <div className={styles.avatarCheckBadge}>
            <F0Icon icon={CheckCircle} size="sm" color="#10B881" />
          </div>
        )}
      </div>
      <Text.Normal size="200" color="grey1000">
        {employee.fullName}
      </Text.Normal>
    </Fragment>
  )
}

interface AssigneesRowProps {
  assignees: ApprovalsV2TimelineEntryAssignee[]
  employees: TimelineItemEmployeeFragment[]
  totalAssigneesCount?: number | null
}

const HOVER_DELAY_MS = 100

const AssigneesRow = ({ assignees, employees, totalAssigneesCount }: AssigneesRowProps) => {
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showTooltip = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setTooltipOpen(true)
  }, [])

  const hideTooltip = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setTooltipOpen(false)
    }, HOVER_DELAY_MS)
  }, [])

  if (!assignees || assignees.length === 0) {
    return null
  }

  const inlineAssignees = assignees.slice(0, INLINE_DISPLAY_LIMIT)
  const overflowAssignees = assignees.slice(INLINE_DISPLAY_LIMIT)
  const total = totalAssigneesCount ?? assignees.length
  const remaining = total - inlineAssignees.length

  const getTooltipStyle = (): React.CSSProperties => {
    if (!triggerRef.current) return {}
    const rect = triggerRef.current.getBoundingClientRect()
    return {
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left + rect.width / 2,
      transform: 'translateX(-50%)',
    }
  }

  return (
    <Box flexDirection="row" alignItems="center" gap="s8" flexWrap="wrap">
      {inlineAssignees.map((assignee, index) => renderAssignee(assignee, index, employees))}
      {remaining > 0 && (
        <div
          ref={triggerRef}
          className={styles.overflowTooltipWrapper}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
        >
          <Box
            className={styles.assigneesBadge}
            flexDirection="row"
            alignItems="center"
            justifyContent="center"
            flexNoShrink
          >
            <Text.Normal size="100" color="grey800" weight="medium">
              +{remaining}
            </Text.Normal>
          </Box>
        </div>
      )}
      {tooltipOpen &&
        createPortal(
          <div
            className={styles.overflowTooltip}
            style={getTooltipStyle()}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
          >
            <OverflowTooltip assignees={overflowAssignees} employees={employees} />
          </div>,
          document.body
        )}
    </Box>
  )
}

export default AssigneesRow
